import type { LoadImagePayload } from '@getaccept/editor-lib';
import { getLoadImageURLPayloads, setImageSrcURL } from '@getaccept/editor-lib';
import { useLoadedImagesStore } from '@getaccept/editor-lib/src/store/loaded-images.store';
import { EditorBlockService } from '@getaccept/editor/src/api/services/editor-block.service';
import { useEditorContentStore } from '@getaccept/editor/src/store/editor-content.store';
import Analytics from '@getaccept/lib-shared/src/analytics';
import { EnumDocumentType } from '@getaccept/lib-shared/src/enums/DocumentType';
import { ExternalEditorType } from '@getaccept/lib-shared/src/enums/ExternalEditorType';
import { DocumentPageType } from '@getaccept/lib-shared/src/enums/document-page-type';
import { ImageHeight } from '@getaccept/lib-shared/src/enums/image-height';
import { WindowSize } from '@getaccept/lib-shared/src/enums/window-size';
import { FieldSource } from '@getaccept/lib-shared/src/fields/types/enums/field-source';
import type { Field } from '@getaccept/lib-shared/src/fields/types/field';
import { t } from '@getaccept/lib-shared/src/helpers/translation.helper';
import { isWindowSize } from '@getaccept/lib-shared/src/helpers/window-size.helper';
import { RecipientRole } from '@getaccept/lib-shared/src/recipients/enums/recipient-role';
import type { DocumentResource } from '@getaccept/lib-shared/src/resources/types/document-resource';
import { useToast } from '@getaccept/lib-shared/src/toast';
import type { Document } from '@getaccept/lib-shared/src/types/Document';
import type { Recipient } from '@getaccept/lib-shared/src/types/Recipient';
import type { Tag } from '@getaccept/lib-shared/src/types/Tag';
import { AnalyticsEvent } from '@getaccept/lib-shared/src/types/analytics';
import type { DocumentAttachment } from '@getaccept/lib-shared/src/types/document-attachment';
import type { DocumentPage } from '@getaccept/lib-shared/src/types/document-page';
import type { EngagementVideo } from '@getaccept/lib-shared/src/types/engagement-video';
import type { UpdateDocumentPatch } from '@getaccept/lib-shared/src/types/update-document-patch';
import type { User } from '@getaccept/lib-shared/src/users/types/user';
import type { ComputedRef, Ref } from '@vue/composition-api';
import { computed, ref } from '@vue/composition-api';
import { defineStore } from 'pinia';
import { TagService } from '../../../api/tags/services/tag.service';
import { DocumentAttachmentService } from '../../../api/attachments/document-attachments/services/document-attachment.service';
import { DocumentHelper } from '../../../api/documents/helpers/document.helper';
import { DocumentService } from '../../../api/documents/services/document.service';
import { RecipientService } from '../../../api/recipients/services/recipient.service';
import type { ImplementResourceDTO } from '../../../api/resources/dtos/implement-resource-dto';
import { ResourceService } from '../../../api/resources/services/resource.service';
import { SigningOrderHelper } from '../../recipients/helpers/signing-order.helper';
import { DocumentManipulatorHelper } from '../../../document-manipulator/helpers/document-manipulator.helper';
import { useFieldsStore } from '../fields/store/fields.store';
import { AssemblerHelper } from '../helpers/assembler.helper';
import { useRecipientDetailsStore } from '../recipients/store/recipient-details.store';
import type { PreviewToken } from '../types/enums/preview-token';
import { getDefaultState } from './assembler.constants';

export const useAssemblerStore = defineStore('assembler', () => {
  const document: Ref<Document> = ref(null);
  const loaded = ref(false);
  const loadDocumentSuccess = (_document: Document) => {
    document.value = _document;
    loaded.value = true;
    loadDocumentResources();
  };

  const loading = ref(false);
  const loadDocument = async (documentId: string) => {
    loading.value = true;
    try {
      const documentResponse: Document = await DocumentService.loadDocument(documentId);
      if (documentResponse?.type === EnumDocumentType.DSR) {
        useToast().toast.danger(t('failed-to-load-document'));
        return;
      }
      loadDocumentSuccess(documentResponse);
    } catch (error) {
      useToast().toast.danger(t('failed-to-load-document'));
    } finally {
      loading.value = false;
    }
  };

  const reloadDocument = async (documentId: string) => {
    try {
      const documentResponse: Document = await DocumentService.loadDocument(documentId);
      useEditorContentStore().resetBlocks();
      documentResponse.pages
        .filter(page => page.editorBlock)
        .forEach(page =>
          useEditorContentStore().setBlock(
            useLoadedImagesStore().mapImagesToBlock(page.editorBlock)
          )
        );
      loadDocumentSuccess(documentResponse);
    } catch (error) {
      useToast().toast.danger(t('failed-to-load-document'));
    }
  };

  const showEditDocumentPage = ({
    isInEditMode,
    pageId,
  }: {
    isInEditMode: boolean;
    pageId: string;
  }) => {
    const pages = document.value.pages.map((statePage: DocumentPage) =>
      statePage.id === pageId
        ? { ...statePage, isInEditMode }
        : { ...statePage, isInEditMode: false }
    );
    document.value = { ...document.value, pages };
  };

  const setUniquePages = (pages: DocumentPage[]) => {
    const uniquePages: DocumentPage[] = pages.reduce((acc: DocumentPage[], curr: DocumentPage) => {
      const exists = !!acc.find((page: DocumentPage) => page.id === curr.id);
      return exists ? acc : [...acc, curr];
    }, []);
    document.value = { ...document.value, pages: uniquePages };
  };

  const setUpdatedRecipient = (updatedRecipient: Recipient) => {
    const recipients =
      !!document.value?.recipients?.length &&
      !!document.value.recipients.some(
        (stateRecipient: Recipient) => stateRecipient.id === updatedRecipient.id
      )
        ? document.value.recipients.map((stateRecipient: Recipient) =>
            stateRecipient.id === updatedRecipient.id ? updatedRecipient : stateRecipient
          )
        : [...document.value.recipients, updatedRecipient];

    document.value = { ...document.value, recipients };
  };

  const updateRecipient = async ({
    id,
    properties,
  }: {
    id: string;
    properties: Partial<Recipient>;
  }) => {
    const oldRecipient: Recipient = document.value.recipients.find(
      (recipient: Recipient) => recipient.id === id
    );
    const updatedRecipient: Recipient = { ...oldRecipient, ...properties };
    setUpdatedRecipient(updatedRecipient);
    const recipientStore = useRecipientDetailsStore();
    try {
      await RecipientService.updateRecipient(id, document.value.id, properties);
      recipientStore.setModalVisibility(false);
    } catch (error) {
      setUpdatedRecipient(oldRecipient);
    }
  };

  const couldNotUpdateMessage = 'could-not-update-document';

  const updateDocument = async (patch: UpdateDocumentPatch) => {
    const previousDocument: Document = { ...document.value };
    const unsignableDocument: boolean = patch.isSigning !== undefined && !patch.isSigning;
    if (unsignableDocument && document.value?.recipients?.length) {
      const recipientsToUpdate: Recipient[] = AssemblerHelper.getSignableRecipients(
        document.value.recipients
      );
      recipientsToUpdate.forEach((recipient: Recipient) =>
        updateRecipient({
          id: recipient.id,
          properties: { role: RecipientRole.CC },
        })
      );
    }

    try {
      const updatedDocument = DocumentHelper.updatedDocumentFromPatch(document.value, patch);
      document.value = updatedDocument;
      await DocumentService.updateDocument(patch, document.value.id);
    } catch (error) {
      document.value = previousDocument;
      useToast().toast.danger(t(couldNotUpdateMessage));
    }
  };

  const currentDocumentPage: Ref<DocumentPage> = ref(null);
  const setCurrentDocumentPage = (page: DocumentPage) => {
    currentDocumentPage.value = page;
  };

  const removePage = (page: DocumentPage) => {
    const originalPages: DocumentPage[] = document.value.pages;
    const filteredPages: DocumentPage[] = DocumentManipulatorHelper.filterPage(
      page,
      document.value.pages
    );
    setUniquePages(filteredPages);
    if (!filteredPages.length) {
      updateDocument({
        name: '',
        externalEditorId: '',
        externalEditorType: ExternalEditorType.None,
      });
      return;
    }
    const deletedPageIndex: number = originalPages.findIndex(
      (originalPage: DocumentPage) => originalPage.id === page.id
    );
    currentDocumentPage.value = AssemblerHelper.getCurrentDocumentPageAfterPageDelete(
      deletedPageIndex,
      filteredPages
    );
  };

  const resetState = () => {
    document.value = getDefaultState().document;
    loaded.value = getDefaultState().loaded;
    loading.value = getDefaultState().loading;
    currentDocumentPage.value = getDefaultState().currentDocumentPage;
    documentSettingsVisible.value = getDefaultState().documentSettingsVisible;
    selectedRecipient.value = getDefaultState().selectedRecipient;
    selectedUser.value = getDefaultState().selectedUser;
    previewToken.value = getDefaultState().previewToken;
    sidebarCollapsed.value = getDefaultState().sidebarCollapsed;
    activeContentSelector.value = getDefaultState().activeContentSelector;
    addRoleModalVisible.value = getDefaultState().addRoleModalVisible;
    pageToBeReplaced.value = getDefaultState().pageToBeReplaced;
    updatingDimensions.value = getDefaultState().updatingDimensions;
  };

  const documentSettingsVisible = ref(false);

  const showDocumentSettings = (show: boolean) => {
    documentSettingsVisible.value = show;
  };

  const selectedRecipient: Ref<{ id: string; connectFirstRecipient: boolean }> = ref({
    id: null,
    connectFirstRecipient: null,
  });
  const selectedUser: Ref<User> = ref(null);
  const setSelectedUser = (user: User) => {
    selectedRecipient.value = {
      id: null,
      connectFirstRecipient: false,
    };
    selectedUser.value = user;
  };

  const setSelectedRecipient = ({
    recipient,
    connectFirstRecipient,
  }: {
    recipient?: Recipient;
    connectFirstRecipient: boolean;
  }) => {
    selectedUser.value = null;
    selectedRecipient.value = {
      id: recipient ? recipient.id : null,
      connectFirstRecipient,
    };
  };

  const updateRecipientApproversToCC = () => {
    const approverRecipients: Recipient[] = document.value.recipients.filter(
      (recipient: Recipient) =>
        recipient.role === RecipientRole.Approver ||
        recipient.role === RecipientRole.ExternalApprover
    );

    approverRecipients.forEach(async (oldRecipient: Recipient) => {
      const updatedRecipient: Recipient = { ...oldRecipient, role: RecipientRole.CC };
      setUpdatedRecipient(updatedRecipient);
      try {
        await RecipientService.updateRecipient(oldRecipient.id, document.value.id, {
          role: RecipientRole.CC,
        });
      } catch {
        setUpdatedRecipient(oldRecipient);
      }
    });
  };

  const reassignFields = async ({
    connectedFields,
    recipientId,
  }: {
    connectedFields: Field[];
    recipientId: string;
  }) => {
    const updatedFields = AssemblerHelper.reassignFields(
      connectedFields,
      recipientId,
      !!selectedRecipient?.value.connectFirstRecipient
    );
    const fieldsStore = useFieldsStore();
    await fieldsStore.update(updatedFields);
  };

  const removeRecipientSuccess = (recipientId: string) => {
    const fieldsStore = useFieldsStore();
    fieldsStore.load(document.value.id);
    if (selectedRecipient.value.id === recipientId) {
      setSelectedRecipient({
        recipient: null,
        connectFirstRecipient: false,
      });
      setSelectedUser(document.value.user);
    }
  };

  const removeRecipient = async (recipientId: string) => {
    const fieldsStore = useFieldsStore();
    const connectedFields: Field[] = fieldsStore.fields.filter(
      field => field.recipientId === recipientId
    );
    const existingRecipients: Recipient[] = document.value.recipients;
    let remainingRecipients: Recipient[] = existingRecipients.filter(
      (recipient: Recipient) => recipient.id !== recipientId
    );

    if (remainingRecipients.length > 1) {
      remainingRecipients = SigningOrderHelper.fixRecipientsGaps(remainingRecipients);
    }
    try {
      document.value = { ...document.value, recipients: remainingRecipients };
      const hasSigner: boolean = document.value.recipients.some(
        (recipient: Recipient) => recipient.role === RecipientRole.Signer
      );

      const hasApprover: boolean = document.value.recipients.some(
        (recipient: Recipient) =>
          recipient.role === RecipientRole.Approver ||
          recipient.role === RecipientRole.ExternalApprover
      );

      if (!hasSigner && hasApprover) {
        updateRecipientApproversToCC();
      }

      await RecipientService.deleteRecipient(recipientId);
      if (remainingRecipients.length === 1) {
        updateDocument({ isSigningOrder: 0 });
      }

      if (connectedFields.length && remainingRecipients.length) {
        await reassignFields({
          connectedFields,
          recipientId: remainingRecipients[0].id,
        });
        useToast().toast.warning(t('remove_recipient_warning_toaster'));
      }
      await removeRecipientSuccess(recipientId);
    } catch (error) {
      document.value = { ...document.value, recipients: existingRecipients };
      if (connectedFields.length) {
        reassignFields({
          connectedFields,
          recipientId,
        });
      }
    }
  };

  const firstAddedRecipient: ComputedRef<Recipient> = computed(() => {
    if (!document.value?.recipients?.length) {
      return;
    }
    return document.value.recipients.reduce((prev: Recipient, curr: Recipient) =>
      new Date(prev.createdAt) < new Date(curr.createdAt) ? prev : curr
    );
  });

  const connectFirstRecipientToFields = () => {
    const fieldsStore = useFieldsStore();
    const fieldsWithMissingRecipient: Field[] = fieldsStore.fields.filter(
      (field: Field) => field.source === FieldSource.Recipient && !field.recipientId
    );

    if (firstAddedRecipient.value && !!fieldsWithMissingRecipient.length) {
      const updatedFields: Field[] = fieldsWithMissingRecipient.map((field: Field) => ({
        ...field,
        recipientId: firstAddedRecipient.value.id,
      }));

      if (selectedRecipient?.value.connectFirstRecipient) {
        setSelectedRecipient({
          recipient: firstAddedRecipient.value,
          connectFirstRecipient: false,
        });
      }
      const fieldsStore = useFieldsStore();
      fieldsStore.update(updatedFields);
    }
  };

  const loadRecipients = async () => {
    try {
      const recipients: Recipient[] = await RecipientService.loadRecipients(document.value.id);
      document.value = { ...document.value, recipients };
      if (recipients.length === 1) {
        connectFirstRecipientToFields();
      }
    } catch {
      useToast().toast.danger(t('failed-to-load-recipients'));
    }
  };

  const loadDocumentAttachments = async (documentId: string) => {
    const originalDocumentAttachments = document.value.documentAttachments;
    try {
      const documentAttachment: DocumentAttachment[] = await DocumentAttachmentService.load(
        documentId
      );
      document.value.documentAttachments = documentAttachment;
    } catch (e) {
      document.value.documentAttachments = originalDocumentAttachments;
      useToast().toast.danger(t('could-not-load-attachments'));
    }
  };

  const refreshRecipients = (documentId: string) => {
    loadRecipients();
    loadDocumentAttachments(documentId);
  };

  const addNewRecipient = async ({
    recipient,
    documentId,
    file,
  }: {
    recipient: Recipient;
    documentId: string;
    file: File;
  }) => {
    const recipientStore = useRecipientDetailsStore();
    recipientStore.setIsLoading(true);
    let newRecipient = recipient;
    try {
      if (file) {
        const thumbUrl = await RecipientService.updateRecipientThumbnail(file, recipient.email);
        newRecipient = { ...newRecipient, thumbUrl };
      }
      const recipientId: string = await RecipientService.saveRecipient(newRecipient, documentId);
      await RecipientService.updateRecipient(recipientId, documentId, newRecipient);
      await loadRecipients();
      if (
        recipient.role === RecipientRole.Approver ||
        recipient.role === RecipientRole.ExternalApprover
      ) {
        if (!document.value.isSigningOrder) {
          document.value.isSigningOrder = 1;
        }
        const initialRecipients = [...document.value.recipients];

        const sortedRecipients = AssemblerHelper.sortRecipients(initialRecipients);

        const recipientOrder: Partial<Recipient>[] = sortedRecipients.map(
          (recipient: Recipient, index: number) => ({
            id: recipient.id,
            orderNum: index + 1,
          })
        );
        await RecipientService.updateRecipientOrder(documentId, recipientOrder);
      }
      refreshRecipients(documentId);
      recipientStore.setModalVisibility(false);
      useToast().toast.success(t('successfully-added-recipient', { email: newRecipient.email }));
    } catch (error) {
      useToast().toast.danger(t('could-not-add-recipient'));
    } finally {
      recipientStore.setIsLoading(false);
    }
  };

  const updateRecipientLegacy = async ({
    recipient,
    documentId,
    file,
  }: {
    recipient: Recipient;
    documentId: string;
    file: File;
  }) => {
    let oldRecipient: Recipient = {};
    if (recipient.id) {
      oldRecipient = document.value.recipients.find(
        (stateRecipient: Recipient) => stateRecipient.id === recipient.id
      );
    }
    const recipientStore = useRecipientDetailsStore();
    recipientStore.setIsLoading(true);
    try {
      let newRecipient = recipient;
      if (file) {
        const thumbUrl = await RecipientService.updateRecipientThumbnail(file, recipient.email);
        newRecipient = { ...newRecipient, thumbUrl };
      }
      await RecipientService.updateRecipient(recipient.id, documentId, newRecipient);

      refreshRecipients(documentId);
      const recipientStore = useRecipientDetailsStore();
      recipientStore.setModalVisibility(false);
    } catch (error) {
      setUpdatedRecipient(oldRecipient);
      useToast().toast.danger(t('could-not-update-recipient'));
    } finally {
      recipientStore.setIsLoading(false);
    }
  };

  const createRecipientSuccess = (recipient: Recipient, documentId: string) => {
    if (recipient.role === RecipientRole.Approver) {
      document.value.isSigningOrder = 1;
    }

    if (document.value.isSigningOrder) {
      const lastRecipient: Recipient =
        document.value?.recipients[document.value.recipients.length - 1];
      const orderNum: number = lastRecipient ? lastRecipient.orderNum + 1 : 1;
      document.value = {
        ...document.value,
        recipients: [...document.value.recipients, { ...recipient, orderNum }],
      };
      updateRecipient({
        id: recipient.id,
        properties: { orderNum },
      });
    } else {
      document.value = { ...document.value, recipients: [...document.value.recipients, recipient] };
    }
    refreshRecipients(documentId);
    connectFirstRecipientToFields();
  };

  const createRecipient = async ({
    contactId,
    documentId,
    role,
  }: {
    contactId: string;
    documentId: string;
    role: RecipientRole;
  }) => {
    try {
      const recipient: Recipient = await RecipientService.createRecipient(
        contactId,
        documentId,
        role
      );
      createRecipientSuccess(recipient, documentId);
    } catch (error) {
      console.error('Could not create recipient', error);
    }
  };

  const updateAttachment = (updatedDocumentAttachment: DocumentAttachment) => {
    const updatedAttachemnts: DocumentAttachment[] = document.value.documentAttachments?.length
      ? document.value.documentAttachments.map((documentAttachment: DocumentAttachment) =>
          documentAttachment.id === updatedDocumentAttachment.id
            ? updatedDocumentAttachment
            : documentAttachment
        )
      : document.value.documentAttachments;
    document.value = { ...document.value, documentAttachments: updatedAttachemnts };
  };

  const removeAttachment = (id: string) => {
    const filteredAttachments: DocumentAttachment[] = document.value.documentAttachments?.length
      ? document.value.documentAttachments.filter(
          (documentAttachment: DocumentAttachment) => documentAttachment.id !== id
        )
      : document.value.documentAttachments;
    document.value = { ...document.value, documentAttachments: filteredAttachments };
  };

  const updateRecipientsSigningOrder = async (recipients: Recipient[]) => {
    const oldRecipients: Recipient[] = [...document.value.recipients];
    const recipientOrder: Partial<Recipient>[] = recipients.map((recipient: Recipient) => ({
      id: recipient.id,
      orderNum: recipient.orderNum,
    }));
    document.value = { ...document.value, recipients };
    try {
      await RecipientService.updateRecipientOrder(document.value.id, recipientOrder);
    } catch (error) {
      document.value = { ...document.value, recipients: oldRecipients };
    }
  };

  const deleteDocument = async (id: string) => {
    try {
      await DocumentService.delete(id);
      useToast().toast.success(t('document-successfully-deleted'));
    } catch {
      useToast().toast.danger(t('failed-to-delete-document'));
    } finally {
      resetState();
    }
  };

  const updateDocumentUser = async ({ documentId, user }: { documentId: string; user: User }) => {
    try {
      await DocumentService.updateDocument({ userId: user.id }, documentId);
      const fieldsStore = useFieldsStore();
      fieldsStore.loadSenderSignature(user.id);
      document.value.user = user;
      if (selectedUser.value) {
        setSelectedUser(user);
      }
    } catch {
      loading.value = false;
      useToast().toast.danger(t('failed-to-change-document-owner'));
    }
  };

  const sortSigningRecipients = () => {
    const isAllSigners = document.value.recipients.every(recipient => recipient.role === 'signer');
    if (isAllSigners) {
      return;
    }
    document.value.recipients
      .filter((recipient: Recipient) => recipient.role == 'signer')
      .forEach((recipient: Recipient, i: number) => {
        updateRecipient({
          id: recipient.id,
          properties: { orderNum: i + 1 },
        });
      });
  };

  const previewToken: Ref<PreviewToken> = ref({
    jwt: null,
    exp: null,
  });
  const getPreviewToken = async () => {
    try {
      const previewTokenResponse = await DocumentService.getPreviewToken(document.value.id);
      previewToken.value = previewTokenResponse;
    } catch {
      useToast().toast.danger(t('failed-to-preview-document'));
    }
  };

  const loadPageThumbUrl = async (pageId: string) => {
    const { thumbUrl }: Partial<DocumentPage> = await DocumentService.loadPageThumbUrl(
      pageId,
      document.value.id
    );
    const pages: DocumentPage[] = document.value.pages.map((page: DocumentPage) =>
      page.id === pageId
        ? {
            ...page,
            thumbUrl,
          }
        : page
    );
    document.value = { ...document.value, pages };
  };

  const deleteTemplate = async (id: string) => {
    try {
      await DocumentService.deleteTemplate(id);
    } catch (error) {
      useToast().toast.danger(t('failed-to-delete-template'));
    }
  };
  const sidebarCollapsed = ref(isWindowSize(WindowSize.Medium));

  const setSidebarCollapsed = (_sidebarCollapsed: boolean) => {
    sidebarCollapsed.value = _sidebarCollapsed;
  };

  const addEngageVideo = (engageVideo: EngagementVideo) => {
    document.value = { ...document.value, video: engageVideo };
  };

  const removeEngageVideo = () => {
    document.value = { ...document.value, video: null };
  };

  const activeContentSelector: Ref<number> = ref(null);
  const setActiveContentSelector = (_activeContentSelector: number) => {
    activeContentSelector.value = _activeContentSelector;
  };

  const loadEditorImageURL = async (loadImagePayload: LoadImagePayload) => {
    try {
      const { url: srcUrl, externalId, blurhash } = await EditorBlockService.getImageUrl(
        loadImagePayload.imageId,
        ImageHeight.QHD
      );
      document.value = setImageSrcURL(document.value as any, {
        ...loadImagePayload,
        srcUrl,
        externalId,
        blurhash,
      }) as Document;
    } catch (err) {
      console.error(err);
    }
  };

  const loadEditorImageURLs = (pages: DocumentPage[]) => {
    const loadImagePayloads = pages
      .filter(({ editorBlock }) => editorBlock)
      .flatMap(({ editorBlock }) => getLoadImageURLPayloads(editorBlock));
    loadImagePayloads.forEach(loadImagePayload => loadEditorImageURL(loadImagePayload));
  };

  const loadDocumentResourcesSuccess = (resources: DocumentResource[]) => {
    const pages: DocumentPage[] = document.value?.pages || [];
    if (!pages.length) {
      return;
    }
    const mappedPages: DocumentPage[] = AssemblerHelper.mapDocumentResources(pages, resources);
    document.value = { ...document.value, pages: mappedPages };
    loadEditorImageURLs(mappedPages.filter(page => page.editorBlock));
  };

  const loadDocumentResources = async () => {
    try {
      const documentResources: DocumentResource[] = await ResourceService.loadDocumentResources({
        documentId: document.value.id,
      });
      loadDocumentResourcesSuccess(documentResources);
    } catch (e) {
      useToast().toast.danger(t('failed-to-fetch-document-resources'));
    }
  };

  const disconnectResource = async ({ documentId, resourceId }) => {
    try {
      const payload: ImplementResourceDTO = { documentId, resourceId };
      await ResourceService.disconnect(payload);
    } catch (error) {
      console.error('Could not disconnect resource', error);
    }
  };

  const updatePage = (updatedPage: DocumentPage) => {
    document.value = {
      ...document.value,
      pages: DocumentManipulatorHelper.updatePage(updatedPage, document.value.pages),
    };
  };

  const addPages = (pages: DocumentPage[]) => {
    activeContentSelector.value = null;
    const mappedPages: DocumentPage[] = DocumentManipulatorHelper.insertNewPages(
      pages,
      document.value.pages
    );
    document.value = { ...document.value, pages: mappedPages };

    const editorResources = pages.filter(page => page.editorBlock && page.resourceId);
    if (editorResources.length) {
      loadEditorImageURLs(editorResources);
    }
    const fieldsStore = useFieldsStore();
    fieldsStore.load(document.value.id);

    if (currentDocumentPage.value) {
      return;
    }
    currentDocumentPage.value = pages.find(
      (page: DocumentPage) => page.pageType === DocumentPageType.DocumentContent
    );
  };

  const addRecipientRoleSuccess = async (recipients: Recipient[]) => {
    try {
      const recipientOrder: Partial<Recipient>[] = AssemblerHelper.orderRecipients(recipients);
      await RecipientService.updateRecipientOrder(document.value.id, recipientOrder);
      await loadRecipients();
    } catch {
      useToast().toast.danger(t('could-not-update-recipient-order'));
    }
  };

  const addRecipientRole = async (payload: Recipient) => {
    try {
      await RecipientService.addRecipient(payload);
      await loadRecipients();
      const initialRecipients = [...document.value.recipients];
      const recipients: Recipient[] = AssemblerHelper.sortRecipients(initialRecipients);
      addRecipientRoleSuccess(recipients);
    } catch (e) {
      console.log(e);
      useToast().toast.danger(t('could-not-add-template-role'));
    }
  };

  const updateRecipientRole = async (recipient: Recipient) => {
    await RecipientService.updateRecipient(recipient.id, document.value.id, recipient);
    await loadRecipients();
  };

  const addRoleModalVisible = ref(false);
  const showAddRoleModal = (show: boolean) => {
    addRoleModalVisible.value = show;
  };

  const addGoogleDocsPages = (pages: DocumentPage[]) => {
    document.value = { ...document.value, pages };
    const fieldsStore = useFieldsStore();
    fieldsStore.load(document.value.id);
  };

  const addDocument = async (id: string) => {
    await reloadDocument(id);
    activeContentSelector.value = null;
    const fieldsStore = useFieldsStore();
    fieldsStore.load(document.value.id);
    connectFirstRecipientToFields();
    if (currentDocumentPage.value) {
      return;
    }
    currentDocumentPage.value = document.value.pages.find(
      (page: DocumentPage) => page.pageType === DocumentPageType.DocumentContent
    );
  };

  const pageToBeReplaced: Ref<DocumentPage> = ref(null);
  const replacePage = (page: DocumentPage) => {
    pageToBeReplaced.value = page;
    const updatedPages: DocumentPage[] = DocumentManipulatorHelper.filterPage(
      page,
      document.value.pages
    );
    setUniquePages(updatedPages);
    activeContentSelector.value = page.pageNum - 1;
  };

  const cancelReplacePage = () => {
    const updatedPages: DocumentPage[] = DocumentManipulatorHelper.insertNewPages(
      [pageToBeReplaced.value],
      document.value?.pages.length ? [...document.value.pages] : []
    );
    setUniquePages(updatedPages);
    pageToBeReplaced.value = getDefaultState().pageToBeReplaced;
  };

  const resetReplacePage = () => {
    pageToBeReplaced.value = getDefaultState().pageToBeReplaced;
  };

  const loadExternalEditorId = async (documentId: string) => {
    try {
      const externalEditorId: string = await DocumentService.loadExternalEditorId(documentId);
      document.value = { ...document.value, externalEditorId };
    } catch {
      useToast().toast.danger(t(couldNotUpdateMessage));
    }
  };

  const tagFailed = (tags: Tag[], message: string) => {
    document.value = { ...document.value, tags };
    useToast().toast.danger(message);
  };

  const addTag = async (tag: Tag) => {
    const oldTags: Tag[] = [...document.value.tags];
    const tags: Tag[] = [...document.value.tags, tag];
    document.value = { ...document.value, tags };
    try {
      await TagService.addMultiple([document.value.id], [tag.id]);
      await TagService.legacyUpdateDocument(document.value.id, tags);
    } catch (error) {
      tagFailed(oldTags, t('could-not-add-tag'));
    }
  };

  const removeTag = async (tag: Tag) => {
    const oldTags: Tag[] = [...document.value.tags];
    const tags: Tag[] = document.value.tags.filter((oldTag: Tag) => oldTag.id !== tag.id);
    document.value.tags = tags;
    try {
      await TagService.removeMultiple([document.value.id], [tag.id]);
      await TagService.legacyUpdateDocument(document.value.id, tags);
    } catch (error) {
      tagFailed(oldTags, t('could-not-remove-tag'));
    }
  };

  const updateEngagementSection = async (patch: UpdateDocumentPatch) => {
    const oldDocument: Document = { ...document.value };
    const updatedDocument = DocumentHelper.updatedDocumentFromPatch(document.value, patch);
    document.value = updatedDocument;
    try {
      await DocumentService.updateDocument(patch, document.value.id);
    } catch (error) {
      document.value = oldDocument;
      useToast().toast.danger(t(couldNotUpdateMessage));
    }
  };

  const addAttachments = (attachments: DocumentAttachment[]) => {
    const documentAttachments: DocumentAttachment[] = document.value.documentAttachments?.length
      ? [...document.value.documentAttachments, ...attachments]
      : attachments;
    document.value = { ...document.value, documentAttachments };
    Analytics.track(AnalyticsEvent.AddedAttachment);
  };

  const updatingDimensions = ref(false);
  const setDimensionsUpdating = (updating: boolean) => {
    updatingDimensions.value = updating;
  };

  const documentRecipients: ComputedRef<Recipient[]> = computed(() => {
    if (!document.value?.recipients?.length) {
      return [];
    }
    return [...document.value.recipients].sort(
      (a: Recipient, b: Recipient) => a.orderNum - b.orderNum
    );
  });

  const templateRoles: ComputedRef<Recipient[]> = computed(() => {
    if (!documentRecipients.value.length) {
      return [];
    }
    return documentRecipients.value.filter((recipient: Recipient) => {
      const emptyRecipient = !(
        recipient.firstName ||
        recipient.lastName ||
        recipient.email ||
        recipient.phone ||
        recipient.mobile
      );
      return !!recipient.roleName && emptyRecipient;
    });
  });

  const documentHasApprover = computed(() => {
    if (!document.value?.recipients?.length) {
      return false;
    }
    return document.value.recipients.some(
      (recipient: Recipient) =>
        recipient.role === RecipientRole.Approver ||
        recipient.role === RecipientRole.ExternalApprover
    );
  });

  const selectedRecipientComputed = computed(() => {
    if (document.value?.recipients?.length && selectedRecipient.value?.id) {
      return document.value.recipients.find(
        (recipient: Recipient) => recipient.id === selectedRecipient.value.id
      );
    }
    return null;
  });

  return {
    document,
    loading,
    loaded,
    documentSettingsVisible,
    currentDocumentPage,
    selectedUser,
    previewToken,
    activeContentSelector,
    pageToBeReplaced,
    selectedRecipient,
    updatingDimensions,
    sidebarCollapsed,
    addRoleModalVisible,
    documentHasApprover,
    templateRoles,

    addPages,
    setUniquePages,
    addDocument,
    removePage,
    updatePage,
    showEditDocumentPage,
    updateDocument,
    setCurrentDocumentPage,
    deleteDocument,
    deleteTemplate,
    updateDocumentUser,
    showDocumentSettings,
    loadDocumentAttachments,
    loadExternalEditorId,
    getPreviewToken,
    loadPageThumbUrl,
    setActiveContentSelector,
    disconnectResource,
    replacePage,
    resetReplacePage,
    cancelReplacePage,
    addTag,
    removeTag,
    removeAttachment,
    updateAttachment,
    loadEditorImageURL,
    setDimensionsUpdating,
    updateRecipientApproversToCC,
    createRecipient,
    setSelectedUser,
    setSelectedRecipient,
    removeRecipient,
    updateRecipient,
    addNewRecipient,
    updateRecipientLegacy,
    updateRecipientsSigningOrder,
    addGoogleDocsPages,
    loadDocument,
    resetState,
    sortSigningRecipients,
    setSidebarCollapsed,
    addEngageVideo,
    removeEngageVideo,
    addRecipientRole,
    showAddRoleModal,
    updateRecipientRole,
    updateEngagementSection,
    addAttachments,
    recipients: documentRecipients,
    selectedRecipientComputed,
  };
});
